<template>
    <v-dialog
        :value="isOpen"
        scrollable
        max-width="900px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template>
                        Edycja użytkownika <strong>{{ userName }}</strong>
                    </template>
                </h1>
            </v-card-title>

            <v-card-text class="text-center">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="username"
                                label="Nazwa użytkownika"
                                :error-messages="usernameErrors"
                                outlined
                                dense
                                required
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="email"
                                label="E-mail"
                                outlined
                                dense
                                :error-messages="emailErrors"
                                required
                            />
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-text-field
                                v-model="profile.phoneNumber"
                                v-mask="'+###########'"
                                outlined
                                dense
                                label="Numer telefonu"
                                @input="changePhoneNumber"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="firstName"
                                label="Imię"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="lastName"
                                label="Nazwisko"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="password"
                                label="Nowe hasło"
                                :type="showPassword ? 'text' : 'password'"
                                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                counter
                                hint="Ilość znaków 8-20, duża litera, cyfra oraz znak specjalny."
                                persistent-hint
                                :error-messages="passwordErrors"
                                autocomplete="new-password"
                                outlined
                                dense
                                @click:append="showPassword = !showPassword"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="confirmedNewPassword"
                                label="Powtórz hasło"
                                :type="showPasswordRepeat ? 'text' : 'password'"
                                :append-icon="showPasswordRepeat ? 'mdi-eye' : 'mdi-eye-off'"
                                counter
                                outlined
                                dense
                                :error-messages="confirmedNewPasswordError"
                                autocomplete="new-password"
                                @click:append="showPasswordRepeat = !showPasswordRepeat"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-autocomplete
                                v-model="profile.productionDepartmentId"
                                :items="productionDepartments"
                                label="Dział"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono działu"
                                outlined
                                dense
                                :disabled="actionLoading.changeProductionDepartment"
                                @change="changeProductionDepartment"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="profile.productionSubDepartmentId"
                                :items="productionSubDepartments"
                                :disabled="!profile.productionDepartmentId"
                                item-value="id"
                                label="Wydział"
                                outlined
                                dense
                            >
                                <template slot="selection" slot-scope="data">
                                    {{ data.item.name }}
                                </template>

                                <template slot="item" slot-scope="data">
                                    {{ data.item.name }}
                                </template>
                            </v-select>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" sm="4">
                            <v-select
                                v-model="profile.positionId"
                                :items="positions"
                                label="Stanowisko"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono stanowiska"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" sm="3">
                            <v-select
                                v-model="profile.squadId"
                                :items="squads"
                                label="Brygada"
                                item-value="id"
                                item-text="name"
                                no-data-text="Nie znaleziono brygady"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" sm="5">
                            <v-autocomplete
                                v-model="profile.supervisorId"
                                :items="users"
                                label="Przełożony"
                                :filter="customUsersFilter"
                                :loading="usersSearchLoading"
                                :search-input.sync="usersSearch"
                                class="v-text-field--placeholder-dark"
                                dense
                                item-value="id"
                                no-data-text="Nie znaleziono"
                                clearable
                                outlined
                                @click:clear="$nextTick(() => profile.supervisorId = null)"
                            >
                                <template slot="no-data">
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!usersSearch">
                                                Wpisz treść aby odnaleźć <strong>przełożonego</strong>.
                                            </template>
                                            <template v-else-if="usersSearchLoading">
                                                Trwa wyszukiwanie...
                                            </template>
                                            <template v-else>
                                                Nie znaleziono
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                                <template slot="selection" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ data.item.username }}
                                    </template>
                                </template>
                                <template slot="item" slot-scope="data">
                                    <template v-if="data.item.firstName && data.item.lastName">
                                        {{ data.item.firstName }} {{ data.item.lastName }}
                                    </template>
                                    <template v-else>
                                        {{ data.item.username }}
                                    </template>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="profile.costCenterNumberId"
                                label="MPK"
                                :items="costCenterNumbers"
                                :search-input.sync="searchCostCenterNumber"
                                item-value="id"
                                item-text="verboseName"
                                no-data-text="Nie znaleziono MPK"
                                outlined
                                dense
                                required
                                clearable
                                hide-details
                                @change="searchCostCenterNumber = null"
                                @click:clear="$nextTick(() => profile.costCenterNumberId = null)"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="profile.machine"
                                :items="machines"
                                :search-input.sync="searchMachine"
                                label="Maszyny:"
                                placeholder="Wybierz maszyny"
                                item-value="id"
                                item-text="name"
                                chips
                                clearable
                                multiple
                                outlined
                                dense
                                hide-details
                                :loading="actionLoading.changeProductionDepartment"
                                @change="searchMachine = null"
                            >
                                <template #selection="data">
                                    <v-chip
                                        close
                                        @update:active="removePropertyMachine(data.item)"
                                    >
                                        {{ data.item.name }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="profile.permissionRoleIds"
                                :items="roles"
                                :search-input.sync="searchRoleId"
                                label="Role w systemie:"
                                placeholder="Wybierz role"
                                item-value="id"
                                item-text="name"
                                chips
                                clearable
                                multiple
                                outlined
                                dense
                                hide-details
                                @change="searchRoleId = null"
                            >
                                <template #selection="data">
                                    <v-chip
                                        close
                                        @update:active="removePropertyRole(data.item)"
                                    >
                                        {{ data.item.name }}
                                    </v-chip>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-container>
                <v-checkbox
                    v-model="profile.mayHaveSubordinate"
                    label="Może mieć podwładnych"
                    hide-details
                />
                <v-checkbox
                    v-model="isActive"
                    label="Czy aktywny?"
                    hide-details
                />
                <v-checkbox
                    v-model="profile.remoteAccess"
                    label="Dostęp zdalny"
                    hide-details
                />
                <v-checkbox
                    v-model="testAccount"
                    label="Konto testowe"
                    hide-details
                />
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    :disabled="actionLoading.saveUser"
                    @click="onClose"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading.saveUser"
                    @click="saveUser(userData.id)"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import customFilters from '@/mixins/customFilters'
import parse from '@/mixins/parse'

import { passwordRequirements } from '@/utils/customValidators'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

import { isEmpty } from 'lodash'
import { required, email, sameAs } from 'vuelidate/lib/validators'
import { mask } from 'vue-the-mask'

let searchTimer

export default {
    name: 'UserDialog',
    directives: { mask },
    mixins: [ customFilters, parse ],
    props: {
        isOpen: {
            type: Boolean
        },
        userData: {
            type: Object,
            default: null
        },
        productionDepartments: {
            type: Array,
            default: null
        }
    },
    data() {
        return {
            username: null,
            firstName: null,
            lastName: null,
            email: null,
            password: null,
            searchRoleId: null,
            searchMachine: null,
            searchCostCenterNumber: null,
            profile: {
                productionDepartmentId: null,
                productionSubDepartmentId: null,
                machine: [],
                positionId: null,
                supervisorId: null,
                squadId: null,
                mayHaveSubordinate: false,
                remoteAccess: false,
                phoneNumber: null,
                permissionRoleIds: [],
                costCenterNumberId: null
            },
            isActive: null,
            testAccount: false,
            confirmedNewPassword: null,
            actionLoading: {
                saveUser: false,
                changeProductionDepartment: false
            },
            showPassword: false,
            showPasswordRepeat: false,
            productionSubDepartments: [],
            usersSearch: null,
            usersSearchLoading: false,
            users: [],
        }
    },
    validations() {
        const validations = {
            username: {
                required
            }
        }

        if (this.email) {
            validations['email'] = {
                email
            }
        }

        if (this.password) {
            validations['password'] = {
                passwordRequirements
            }
            validations['confirmedNewPassword'] = {
                sameAsNewPassword: sameAs('password')
            }
        }

        return validations
    },
    computed: {
        machines() {
            return this.$store.state.configurations.machines.machines
        },
        roles() {
            return this.$store.state.administration.users.roles
        },
        positions() {
            return this.$store.state.administration.users.positions
        },
        squads() {
            return this.$store.state.administration.users.squads
        },
        userName() {
            return `${this.userData.firstName} ${this.userData.lastName}`
        },
        costCenterNumbers() {
            return this.$store.state.administration.costCenterNumbers.costCenterNumbers
        },
        usernameErrors() {
            const errors = []

            if (!this.$v.username.$dirty) return errors
            !this.$v.username.required && errors.push('Nazwa użytkownika jest wymagana.')

            return errors
        },
        emailErrors() {
            if (!this.email) return

            const errors = []

            if (!this.$v.email.$dirty) return errors
            !this.$v.email.email && errors.push('Proszę podać poprawny adres e-mail.')

            return errors
        },
        passwordErrors() {
            if (!this.password) return
            const errors = []

            if (!this.$v.password.$dirty) return errors
            !this.$v.password.passwordRequirements && errors.push('Hasło musi zawierać od 8 do 20 znaków, wielką literę, cyfrę oraz znak specjalny.')

            return errors
        },
        confirmedNewPasswordError() {
            if (!this.password) return
            const errors = []

            if (!this.$v.confirmedNewPassword.$dirty) return errors
            !this.$v.confirmedNewPassword.sameAsNewPassword && errors.push('Hasła nie są identyczne.')

            return errors
        },
    },
    watch: {
        userData(value) {
            if (!isEmpty(value)) {
                this.username = value.username
                this.firstName = value.firstName
                this.lastName = value.lastName
                this.email = value.email
                this.profile.productionDepartmentId = value.profile.productionDepartmentId
                this.profile.machine = value.profile.machine
                this.profile.productionSubDepartmentId = value.profile.productionSubDepartmentId
                this.productionSubDepartments = value.profile.productionDepartment ? value.profile.productionDepartment.productionSubDepartments : []
                this.isActive = value.isActive
                this.profile.positionId = value.profile.positionId
                this.profile.supervisorId = value.profile.supervisorId
                value.profile.supervisor ? this.users.push(value.profile.supervisor) : []
                this.profile.squadId = value.profile.squadId
                this.profile.mayHaveSubordinate = value.profile.mayHaveSubordinate
                this.profile.remoteAccess = value.profile.remoteAccess
                this.testAccount = value.profile.testAccount || value.testAccount || false
                this.profile.phoneNumber = value.profile.phoneNumber
                this.profile.permissionRoleIds = value.profile.permissionRoleIds
                this.profile.costCenterNumberId = value.profile.costCenterNumberId
            }
        },
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        }
    },
    methods: {
        changePhoneNumber(value) {
            this.profile.phoneNumber = this.parseEmptyStringToNull(value)
        },
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: `${query}&mayHaveSubordinate=true`
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        saveUser(id) {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            const payload = {
                username: this.username,
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                password: this.password,
                isActive: this.isActive,
                profile: {
                    ...this.profile,
                    testAccount: this.testAccount
                }
            }

            if (!this.password) {
                delete payload['password']
            }

            this.actionLoading.saveUser = true

            this.$store.dispatch('administration/updateUser', { id, payload })
                .then(() => {
                    this.$emit('on-save')
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.saveUser = false
                    this.onClose()
                })
        },
        removePropertyMachine(item) {
            this.profile.machine.splice(this.profile.machine.indexOf(item.id), 1)
        },
        removePropertyRole(item) {
            this.profile.permissionRoleIds.splice(this.profile.permissionRoleIds.indexOf(item.id), 1)
        },
        changeProductionDepartment() {
            const query = `?productionDepartment_id=${this.profile.productionDepartmentId}`

            this.actionLoading.changeProductionDepartment = true
            this.profile.machine = []

            this.$store.dispatch('configurations/getMachines', { query: query })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.changeProductionDepartment = false
                })
            let department = this.productionDepartments.find(item => item.id === this.profile.productionDepartmentId)

            this.productionSubDepartments = department.productionSubDepartments
            this.profile.productionSubDepartmentId = null
        },
        clearUser() {
            this.username = null
            this.firstName = null
            this.lastName = null,
            this.email = null,
            this.password = null,
            this.profile = {
                productionDepartmentId: null,
                productionSubDepartmentId: null,
                machine: [],
                positionId: null,
                supervisorId: null,
                squadId: null,
                mayHaveSubordinate: false,
                remoteAccess: false,
                permissionRoleIds: [],
                costCenterNumberId: null
            },
            this.isActive = null,
            this.testAccount = false,
            this.confirmedNewPassword = null,
            this.showPassword = false,
            this.showPasswordRepeat = false
        },
        onClose() {
            setTimeout(() => {
                this.clearUser()
                this.users = []
            }, 500)
            this.$v.$reset()

            this.$emit('on-close')
        }
    }
}
</script>
